/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();

      this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      this.watchCarSelectorPopup();

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    handleOutsideClick(evt) {
      const isDrawerOpen =
        this.getAttribute('open') !== null ||
        this.getAttribute('aria-hidden') === 'false' ||
        this.classList.contains('is-open') ||
        this.classList.contains('active');

      if (!isDrawerOpen) return;

      const clickedInsideDrawer = this.contains(evt.target);
      const clickedCartIcon = evt.target.closest('#cart-icon');

      if (!clickedInsideDrawer && !clickedCartIcon) {
        this.close();
      }
    }

    addOutsideClickListener() {
      document.addEventListener('click', this.handleOutsideClick, true);
      document.addEventListener('touchstart', this.handleOutsideClick, true);
    }

    removeOutsideClickListener() {
      document.removeEventListener('click', this.handleOutsideClick, true);
      document.removeEventListener('touchstart', this.handleOutsideClick, true);
    }

    watchCarSelectorPopup() {
      this.handlePopupStateChange = this.checkCarSelectorPopup.bind(this);
      this.carSelectorOpenHandler = () => this.forceCloseDrawer();

      document.addEventListener('car-selector:open', this.carSelectorOpenHandler);

      this.carSelectorObserver = new MutationObserver(() => {
        this.checkCarSelectorPopup();
      });

      const popups = document.querySelectorAll('#myPopup, #banner-model-car, .banner-model-car');
      popups.forEach((popup) => {
        this.carSelectorObserver.observe(popup, {
          attributes: true,
          attributeFilter: ['class', 'style', 'open', 'aria-hidden']
        });
      });

      this.checkCarSelectorPopup();
    }

    checkCarSelectorPopup() {
      const popups = document.querySelectorAll('#myPopup, #banner-model-car, .banner-model-car');

      let isAnyPopupVisible = false;

      popups.forEach((popup) => {
        const styles = window.getComputedStyle(popup);

        const isVisible =
          popup.classList.contains('active') ||
          popup.classList.contains('show') ||
          popup.classList.contains('is-visible') ||
          popup.classList.contains('open') ||
          !popup.classList.contains('hidden') ||
          popup.hasAttribute('open') ||
          popup.getAttribute('aria-hidden') === 'false' ||
          (
            styles.display !== 'none' &&
            styles.visibility !== 'hidden' &&
            styles.opacity !== '0'
          );

        if (isVisible) {
          isAnyPopupVisible = true;
        }
      });

      if (isAnyPopupVisible) {
        this.forceCloseDrawer();
      }
    }

    forceCloseDrawer() {
      const isDrawerOpen =
        this.getAttribute('open') !== null ||
        this.getAttribute('aria-hidden') === 'false' ||
        this.classList.contains('is-open') ||
        this.classList.contains('active');

      if (isDrawerOpen || typeof this.close === 'function') {
        this.close();
      }
    }

    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    open(opener, elementToFocus) {
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';

        // Delay slightly so the opening click does not instantly trigger close
        setTimeout(() => {
          this.addOutsideClickListener();
        }, 0);
      }, quickAddDrawer ? 200 : 0);

      window.initLazyImages();
    }

    close(...args) {
      this.removeOutsideClickListener();
      super.close(...args);
    }

    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      window.initLazyImages();
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    disconnectedCallback() {
      this.removeOutsideClickListener();

      document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.carSelectorOpenHandler) {
        document.removeEventListener('car-selector:open', this.carSelectorOpenHandler);
      }

      if (this.carSelectorObserver) {
        this.carSelectorObserver.disconnect();
      }

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}